// NPM Package Imports
import { Field } from 'formik';
import React from 'react';
import { connect } from 'react-redux';
// Helper Imports
import camelCase from 'lodash/camelCase';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { colors } from 'helpers/ColorHelper';
import { hostIsBoardwalk } from 'helpers/CurrentWindowLocation';
import { getTranslatedString, getLandlordVerbiageOrTranslatedString } from 'helpers/i18n';
import { featureEnabled } from 'helpers/ToggleBot';
import { deepDiver } from 'helpers/utilities';
//UI imports
import { RenderIfToggleOff } from 'components/shared/ToggleRenderer';
import styled from 'styled-components';
import { Switch, Divider } from '@yuhuinc/ui-library';
import Checkbox from '@material-ui/core/Checkbox';

const StyledSwitch = styled(Switch)`
  border-radius: 16px !important;
`;

const StyledDivider = styled(Divider)`
  margin: 13px 0px;
  border: 1px solid #e5e3e0;
`;

const NotificationGroup = ({ notificationType, values, showDivider, setFieldValue }) => {
  let details = getLandlordVerbiageOrTranslatedString(
    `portal.notificationPreferences.${camelCase(notificationType)}Details`
  );
  let heading = getLandlordVerbiageOrTranslatedString(
    `portal.notificationPreferences.${camelCase(notificationType)}`
  );

  if (
    featureEnabled('capreit_specific_notification_preferences') &&
    ['maintenance_requests', 'amenities', 'building_communications'].includes(notificationType)
  ) {
    details = getLandlordVerbiageOrTranslatedString(
      `portal.notificationPreferences.capreit.${camelCase(notificationType)}Details`
    );
    heading = getLandlordVerbiageOrTranslatedString(
      `portal.notificationPreferences.capreit.${camelCase(notificationType)}`
    );
  }

  return (
    <div className="row">
      <h4 style={{ marginBottom: '5px', color: colors.DARK, fontSize: '20px' }}>{heading}</h4>
      <p style={{ color: colors.SLATE_GREY }}>{details}</p>
      {featureEnabled('push_notifications') && (
        <NotificationToggle
          notificationType={notificationType}
          method={'push'}
          values={values}
          setFieldValue={setFieldValue}
        />
      )}
      <NotificationToggle
        notificationType={notificationType}
        method={'email'}
        values={values}
        setFieldValue={setFieldValue}
      />
      <NotificationToggle
        notificationType={notificationType}
        method={'sms'}
        values={values}
        setFieldValue={setFieldValue}
      />
      {showDivider && <StyledDivider />}
    </div>
  );
};

const NotificationToggle = ({ notificationType, method, values, setFieldValue }) => {
  const notification = `notification_preferences.${notificationType}.use_${method}`;
  let disabled = false;

  if (
    featureEnabled('capreit_specific_notification_preferences') &&
    ['maintenance_requests', 'legal_documents'].includes(notificationType) &&
    method === 'email'
  ) {
    disabled = true;
  }

  return (
    <FormControlLabel
      style={{ justifyContent: 'space-between', width: '100%', margin: '7px' }}
      labelPlacement="start"
      control={
        <Field
          name={notification}
          component={StyledSwitch}
          color="primary"
          checked={values.notification_preferences[notificationType][`use_${method}`]}
          disabled={disabled}
          onChange={(value) => {
            setFieldValue(notification, value);
          }}
        />
      }
      label={<span style={{ fontSize: '15px' }}>{getTranslatedString(method)}</span>}
    />
  );
};

const DigitalLegalNotices = ({
  values,
  setFieldValue,
  notificationType,
  tenantInNovaScotia,
  landlordName,
}) => {
  // Nova Scotian tenants that have not had their Agent opt them in to
  // digital legal documents should not see this preference at all.
  if (tenantInNovaScotia && !values.digital_legal_documents) return null;

  const notificationGroup = () => {
    if (!values.digital_legal_documents) return null;

    return (
      <>
        {featureEnabled('push_notifications') && !tenantInNovaScotia && (
          <NotificationToggle
            notificationType={notificationType}
            method={'push'}
            values={values}
            setFieldValue={setFieldValue}
          />
        )}
        <NotificationToggle
          notificationType={notificationType}
          method={'email'}
          values={values}
          setFieldValue={setFieldValue}
        />
        {!tenantInNovaScotia && (
          <NotificationToggle
            notificationType={notificationType}
            method={'sms'}
            values={values}
            setFieldValue={setFieldValue}
          />
        )}
      </>
    );
  };

  const OptInCheckbox = ({ values, setFieldValue }) => {
    if (tenantInNovaScotia) return null;

    return (
      <FormControlLabel
        style={{ width: '100%' }}
        labelPlacement="end"
        control={
          <Field
            id="digital_legal_documents"
            name="digital_legal_documents"
            type="checkbox"
            component={Checkbox}
            checked={values.digital_legal_documents}
            style={{ marginRight: '10px' }}
            onChange={(e) => {
              setFieldValue('digital_legal_documents', e.target.checked);
            }}
          />
        }
        label={
          <span style={{ fontSize: '14px' }}>
            {getTranslatedString('portal.notificationPreferences.legalDocuments.checkboxLabel')}
          </span>
        }
      />
    );
  };

  const labelPrefix = `portal.notificationPreferences`;
  const langParams = {
    landlordName: landlordName,
  };

  const titleText = () => {
    const key = tenantInNovaScotia
      ? `${labelPrefix}.legalDocumentsNovaScotia`
      : `${labelPrefix}.legalDocuments`;
    return getTranslatedString(key, langParams);
  };

  const detailsText = () => {
    const key = tenantInNovaScotia
      ? `${labelPrefix}.legalDocumentsDetailsNovaScotia`
      : `${labelPrefix}.legalDocumentsDetails`;
    return getTranslatedString(key, langParams);
  };

  const iAgreeText = () => {
    const key = `${labelPrefix}.legalDocumentsIAgree`;
    return getTranslatedString(key);
  };

  return (
    <div className="row">
      <h4 style={{ marginBottom: '5px', color: colors.DARK, fontSize: '20px' }}>{titleText()}</h4>
      <p style={{ color: colors.SLATE_GREY }}>{detailsText()}</p>
      {!tenantInNovaScotia && <p style={{ color: colors.SLATE_GREY }}>{iAgreeText()}</p>}
      <OptInCheckbox values={values} setFieldValue={setFieldValue} />
      {notificationGroup()}
      <StyledDivider />
    </div>
  );
};

const NoticeOfEntryAgreement = ({ values, setFieldValue }) => {
  const AgreementCheckbox = ({ values, setFieldValue }) => {
    return (
      <FormControlLabel
        style={{ width: '100%' }}
        labelPlacement="end"
        control={
          <Field
            id="notice_by_email"
            name="notice_by_email"
            type="checkbox"
            component={Checkbox}
            checked={values.notice_by_email}
            style={{ marginRight: '10px' }}
            onChange={(e) => {
              setFieldValue('notice_by_email', e.target.checked);
            }}
          />
        }
        label={<span style={{ fontSize: '14px' }}>{getTranslatedString('iAgree')}</span>}
      />
    );
  };

  let description = (
    <React.Fragment>
      <p style={{ color: colors.SLATE_GREY }}>
        {getTranslatedString('portal.notificationPreferences.noticeOfEntryDetails')}
      </p>
      <br />
      <p style={{ color: colors.SLATE_GREY }}>
        {getTranslatedString('notifications.24hourMaintance')}
      </p>
    </React.Fragment>
  );

  if (featureEnabled('capreit_specific_notification_preferences')) {
    description = (
      <p style={{ color: colors.SLATE_GREY }}>
        {getTranslatedString('portal.notificationPreferences.capreit.noticeOfEntryDetails')}
      </p>
    );
  }

  return (
    <RenderIfToggleOff
      toggle="ui.residents.create_service_request.notice_of_entry_consent"
      dontRenderIf={hostIsBoardwalk()}
    >
      <div className="row">
        <h4
          style={{ marginTop: '27px', marginBottom: '5px', color: colors.DARK, fontSize: '20px' }}
        >
          {getTranslatedString('portal.notificationPreferences.noticeOfEntry')}
        </h4>
        {description}
        <div className="row" style={{ margin: '20px auto 10px' }}>
          <div className="col-xs-12" style={{ padding: '0' }}>
            <AgreementCheckbox values={values} setFieldValue={setFieldValue} />
          </div>
        </div>
      </div>
      <StyledDivider />
    </RenderIfToggleOff>
  );
};

const NotificationPreferencesForm = (props) => {
  const { values, setFieldValue, tenantInNovaScotia, landlordName } = props;
  return (
    <div className="row" style={{ marginBottom: '15px', marginTop: '15px' }}>
      {deepDiver(values, 'notification_preferences.building_communications.visible') && (
        <NotificationGroup
          notificationType={'building_communications'}
          values={values}
          setFieldValue={setFieldValue}
        />
      )}
      {deepDiver(values, 'notification_preferences.legal_documents.visible') && (
        <DigitalLegalNotices
          notificationType={'legal_documents'}
          values={values}
          setFieldValue={setFieldValue}
          tenantInNovaScotia={tenantInNovaScotia}
          landlordName={landlordName}
        />
      )}
      {deepDiver(values, 'notification_preferences.payments.visible') && (
        <NotificationGroup
          notificationType={'payments'}
          values={values}
          setFieldValue={setFieldValue}
        />
      )}
      {deepDiver(values, 'notification_preferences.maintenance_requests.visible') && (
        <NotificationGroup
          notificationType={'maintenance_requests'}
          values={values}
          setFieldValue={setFieldValue}
        />
      )}
      {deepDiver(values, 'notification_preferences.amenities.visible') && (
        <NotificationGroup
          notificationType={'amenities'}
          values={values}
          setFieldValue={setFieldValue}
        />
      )}
      {deepDiver(values, 'notification_preferences.maintenance_requests.visible') && (
        <NoticeOfEntryAgreement values={values} setFieldValue={setFieldValue} />
      )}
      {deepDiver(values, 'notification_preferences.promotions.visible') && (
        <NotificationGroup
          notificationType={'promotions'}
          values={values}
          setFieldValue={setFieldValue}
        />
      )}
      {deepDiver(values, 'notification_preferences.surveys.visible') && (
        <NotificationGroup
          notificationType={'surveys'}
          values={values}
          showDivider={false}
          setFieldValue={setFieldValue}
        />
      )}
    </div>
  );
};

NotificationGroup.defaultProps = {
  showDivider: true,
};

const mapStateToProps = (store) => {
  return {
    landlordName: store.userTypes.landlordName,
    tenantInNovaScotia: !!store.dashboard.tenantOverview.in_nova_scotia,
  };
};

export default connect(mapStateToProps, {})(NotificationPreferencesForm);
